export const FAQ_SECTIONS = [
  "Safety & Privacy",
  "How It Works",
  "Purpose & Impact",
];

export const FAQ_SECTIONS_ITEMS_QUESTIONS = {
  "Safety & Privacy": [
    {
      question: "Is my data safe?",
      answer:
        "Yes. Your video is stored securely and seen only by a qualified NRCPD registered interpreter. We use professional systems that keep your information private and protected. No one outside of Unzippd or other Unzippd staff ever sees your video.",
      labels: ["Privacy", "Security", "DataProtection"],
    },
    {
      question: "Will they see my face?",
      answer:
        "No. The organisation you’re giving feedback to never sees your video. Only registered NRCPD interpreters view it to create an accurate written translation, which is the version the organisation receives so it is anonymous feedback.",
      labels: ["Anonymous", "Privacy", "BSL"],
    },
    {
      question: "Who does the translation?",
      answer:
        "Every video is translated by a qualified NRCPD-registered interpreter. They are experienced professionals who follow a strict code of confidentiality and accuracy.",
      labels: ["NRCPD", "Interpreters", "Translation"],
    },
    {
      question: "Is it confidential?",
      answer:
        "Yes. Your video is treated as private from start to finish. It’s only used to create the translation and is never shared publicly.",
      labels: ["Confidential", "Privacy", "Security"],
    },
    {
      question: "Can I delete or retract my video after sending it?",
      answer:
        "Yes. If you change your mind, just contact us. We’ll delete your video and confirm it’s been removed.",
      labels: ["DeleteVideo", "Privacy", "Support"],
    },
    {
      question: "How long is my video stored for?",
      answer:
        "We keep it only as long as needed to translate and deliver your feedback safely. After that, it’s either deleted or securely archived for a short period in line with our data policy.",
      labels: ["Storage", "DataPolicy", "Security"],
    },
    {
      question: "Who has access to the feedback?",
      answer:
        "Only NRCPD interpreters and authorised Unzippd staff can view the original video. The organisation only receives the written translation, not your face or the video itself.",
      labels: ["Privacy", "Access", "Translation"],
    },
    {
      question: "Is my name or identity shared with the organisation?",
      answer:
        "Only if you choose to include your details. You can give feedback anonymously if you prefer.",
      labels: ["Anonymous", "Identity", "Privacy"],
    },
    {
      question: "What is Cyber Essentials?",
      answer:
        "Cyber Essentials is a UK Government security certification that proves our systems are safe and that your video feedback is protected to a national standard.",
      labels: ["CyberEssentials", "Security", "Certification"],
    },
    {
      question: "What is the Disability Confident Scheme?",
      answer:
        "The Disability Confident Scheme is a UK Government programme that recognises employers who actively support and include disabled people, showing our commitment to accessible, fair services for all disabled people.",
      labels: ["Accessibility", "Inclusion", "DisabilityConfident"],
    },
    {
      question: "Do you use AI?",
      answer:
        "We do not use AI to translate BSL. All translations are done by human NRCPD-registered interpreters to make sure your message is understood accurately. Any AI we use is only for technical improvements in the background — never for interpreting your signing.",
      labels: ["AI", "BSL", "HumanTranslation"],
    },
  ],

  "How It Works": [
    {
      question: "How do I record and send my feedback?",
      answer:
        "If you are responding to a poster simply scan the QR code, or click on the link on the webpage. If it is a survey all questions are also in BSL. If it is an enquiry click on the ‘CONTACT US’ icon, you can record your message in BSL, review it, and press send. The system confirms it’s been uploaded safely.",
      labels: ["Feedback", "QRcode", "BSL"],
    },
    {
      question: "Do I need to download an app?",
      answer:
        "No. Unzippd works straight from your internet browser on any device through links or QR codes or by clicking on the contact icon on the company webpage — no app needed.",
      labels: ["Browser", "NoApp", "Accessibility"],
    },
    {
      question: "Can I use it on my phone or tablet?",
      answer:
        "Yes. You can use any phone, tablet, or computer — any device with a camera and internet connection.",
      labels: ["Mobile", "Tablet", "Accessibility"],
    },
    {
      question: "Do I need an interpreter to use it?",
      answer:
        "No. You sign directly to the camera. Our interpreters work afterwards to translate what you signed and send it to the organisation.",
      labels: ["BSL", "Interpreters", "Accessibility"],
    },
    {
      question: "What if I’m Hard of Hearing or not fluent in BSL?",
      answer:
        "That’s absolutely fine. You can sign, gesture, speak, or use any mix you prefer. Unzippd is a total communication platform anyone who prefers video over written forms is welcome to use it, and our qualified interpreters will translate your message clearly into transcripts.",
      labels: ["HardOfHearing", "Communication", "Accessibility"],
    },
    {
      question: "What if I make a mistake or want to re-record my message?",
      answer:
        "You can re-record as many times as you like before sending. Only your final version is saved.",
      labels: ["Video", "Recording", "Support"],
    },
    {
      question: "Will I get a response to my feedback?",
      answer:
        "If you leave your contact details, the organisation can reply. If you don’t, your feedback is still counted and valued.",
      labels: ["Feedback", "Response", "Support"],
    },
    {
      question: "Do I need to type anything?",
      answer:
        "No. You can give your feedback entirely in sign language. Adding text is optional.",
      labels: ["BSL", "NoTyping", "Accessibility"],
    },
    {
      question: "Can I check the translation?",
      answer:
        "Yes, on some forms you can ask to see or confirm your translation before it’s sent to the organisation — these may be complaint forms or job application forms.",
      labels: ["Translation", "Review", "Forms"],
    },
    {
      question: "What if I don’t want to record my video live?",
      answer:
        "For longer answers we offer a drag and drop feature that allows you to upload pre-recorded videos directly from your photos or gallery.",
      labels: ["Upload", "Video", "Accessibility"],
    },
  ],

  "Purpose & Impact": [
    {
      question: "Why do I need to give feedback?",
      answer:
        "Because your experiences show what really happens for Deaf people. Without feedback, organisations can’t know where barriers exist or what works well. Every message helps build a clearer picture of Deaf access in daily life.",
      labels: ["Feedback", "DeafCommunity", "Accessibility"],
    },
    {
      question: "What difference does it make?",
      answer:
        "Deaf feedback gives organisations evidence they can act on. It helps them see practical problems like booking systems, interpreter requests, or communication gaps that might not appear in written surveys.",
      labels: ["Impact", "Accessibility", "Feedback"],
    },
    {
      question: "How will it help Deaf patients?",
      answer:
        "It allows Deaf patients to describe their experiences directly in BSL so services can improve how they communicate and deliver care. That leads to safer appointments, better understanding, and more trust in NHS staff.",
      labels: ["Healthcare", "BSL", "DeafPatients"],
    },
    {
      question: "How will it help Deaf people in the wider community?",
      answer:
        "When more people share their experiences, it builds data that highlights what needs to change — from healthcare to councils to business. It supports long-term improvements and national awareness.",
      labels: ["Community", "Awareness", "Accessibility"],
    },
    {
      question: "How will it help organisations improve access?",
      answer:
        "It gives them direct evidence from Deaf users instead of assumptions. They can use it to train staff, redesign processes, and make real accessibility changes that last.",
      labels: ["Accessibility", "Training", "Improvement"],
    },
    {
      question: "Does giving feedback make any real change?",
      answer:
        "Yes. Deaf feedback has already led to changes. Each message counts towards a bigger shift in how services are delivered.",
      labels: ["Change", "Impact", "Feedback"],
    },
    {
      question: "Is this only for NHS or will it be available for other companies?",
      answer:
        "Unzippd started with healthcare because access there is vital, but any organisation can use it — councils, universities, event venues, charities, or private companies. It’s about making communication equal everywhere.",
      labels: ["NHS", "Business", "Accessibility"],
    },
    {
      question: "What makes this different from other surveys?",
      answer:
        "It’s built for Deaf people by Deaf people. You sign in BSL, not write in English. The translation is done by registered interpreters so your message keeps its meaning and tone.",
      labels: ["BSL", "DeafCommunity", "Surveys"],
    },
    {
      question: "Why is Unzippd doing this?",
      answer:
        "Because Deaf people deserve to be understood in their own language. Unzippd was created to remove the barriers that written systems create and to make communication between Deaf people and organisations easier.",
      labels: ["AboutUs", "Accessibility", "Communication"],
    },
  ],
};

/** HTML id for anchor targets, e.g. section-safety-privacy */
export const getFaqSectionElementId = (title) =>
  `section-${title
    .toLowerCase()
    .replace(/\s*&\s*/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`;

/** Turn pill ids like "DataProtection" or "NoApp" into readable text; leaves "BSL", "AI", "NRCPD" as-is. */
export const formatFaqLabel = (label) =>
  label.replace(/([a-z])([A-Z])/g, "$1 $2");
